import { useMemo } from 'react';
import { useStore } from 'react-redux';

import { capitalizeFirstLetterofWordPlans } from '../../../common/Helpers/capitalize';
import { priceFormat } from '../../../common/Helpers/pricing';

export const useCheckoutHooks = (isLoaded) => {
  const existingState = useStore().getState();
  const { checkoutNew: checkout, plansReferenceData, currentView } = existingState;
  const { cart, errors, errorInfo } = checkout;
  const lineDetails = cart?.lineDetails || { lineInfo: {}, allLines: [] };
  const totalDueToday = cart?.orderDetails?.totalDueToday || 0;
  const isPaymentNeeded = totalDueToday > 0.0 || false;
  const cartCreatorInfo = cart?.cartHeader?.cartCreator || '';
  const accountNumber = cart?.cartHeader?.fullAccountNumber || '';
  const planData = plansReferenceData?.output || {};
  const approvalStatus = cart?.orderDetails?.orderList?.[0]?.creditApplication?.creditApprovalStatus;
  const { acctMcsSubscription = [], payments = {}, allLines = [] } = lineDetails;
  const isManual = '';

  // console.log('CARTHEADER', accountNumber);
  const getPlanInfo = (newPricePlanInfo, planDetails, visFeature, serviceInfo, totalPriceOfPlanAndPerks) => {
    const planName = window?.vzFlags?.isPrepay
      ? newPricePlanInfo?.pricePlanDesc?.replace('GB', ' GB')
      : capitalizeFirstLetterofWordPlans(newPricePlanInfo?.pricePlanDesc);
    const formattedPlanName = newPricePlanInfo?.pricePlanDisplayName || '';
    const MHPromo = serviceInfo?.planDetails?.planPromotions?.promos?.find(
      (promo) => promo && promo.description && promo.description.toLowerCase()?.indexOf('mobile+home') !== -1,
    );
    const mnhDiscount = parseFloat(MHPromo?.amountOff);
    const isLoyality =
      (visFeature && visFeature.find((feature) => feature?.featureDesc?.toLowerCase()?.indexOf('mobile+home loyalty dscnt') !== -1)) || null;
    const { pricePlanCode } = newPricePlanInfo;
    const planCategoryName = newPricePlanInfo?.pricePlanCategoryName;
    const byunlPlan = planCategoryName === 'BYUNL';
    const secondNumberPlan = planCategoryName === 'SECONDPLAN';
    let planPrice = byunlPlan ? parseFloat(totalPriceOfPlanAndPerks) : planDetails?.discountedPrice || planDetails?.planPrice;

    let strikeOffPrice = null;
    if (MHPromo) {
      strikeOffPrice = byunlPlan
        ? `${priceFormat(parseFloat(totalPriceOfPlanAndPerks) + mnhDiscount)}/mo`
        : `${priceFormat(planDetails?.planPrice)}/mo`;
      planPrice = byunlPlan ? parseFloat(totalPriceOfPlanAndPerks) : planDetails?.planPrice - mnhDiscount;
      if (Object.keys(isLoyality || {})?.length > 0) {
        planPrice -= parseFloat(
          isLoyality?.featurePrice?.toString()?.startsWith('-') ? isLoyality?.featurePrice?.split('-')[1] : isLoyality?.featurePrice,
        );
      }
    }

    planPrice = `${priceFormat(planPrice)}/mo`;
    if (secondNumberPlan && planDetails?.discountedPrice) {
      planPrice = `${priceFormat(planDetails?.planPrice)}/mo`;
      strikeOffPrice = `${priceFormat(planDetails?.discountedPrice)}/mo`;
    }
    const planPromotionMessage = null;
    // if (planDetails?.planPromotions?.promos && planDetails?.planPromotions?.promos.length > 0) {
    //   const cdExists = planDetails?.planPromotions?.promos.some((item) => item.id === '19979');
    //   if (cdExists) {
    //     planPromotionMessage = 'Your phone plan gets you 50% off a plan for your connected device.';
    //   }
    // }
    const disneyPlusPromo = planDetails?.disneyPlusPromo;
    return {
      planName,
      planPrice,
      strikeOffPrice,
      planPromotionMessage,
      pricePlanCode,
      disneyPlusPromo,
      formattedPlanName,
      accountNumber,
    };
  };

  const getAccessories = (accessories) => {
    const accessoriesList = accessories.map((accessory) => {
      const { sorId, productId, cartItemType, qty } = accessory;

      let planPrice = cartItemType === 'ACCESSORY' ? parseFloat(accessory.itemPrice * qty) : accessory.itemPrice;
      let strikeOffPrice = null;
      if (accessory.totalPriceWithDiscount) {
        planPrice = `${priceFormat(planPrice)}/mo`;
        strikeOffPrice = `${priceFormat(accessory?.totalPriceWithDiscount)}/mo`;
      } else {
        planPrice = `${priceFormat(planPrice)}/mo`;
      }

      return {
        sorId,
        productId,
        deviceDisplayName: accessory.deviceDisplayName,
        imageUrl: accessory.imageUrlMap?.largeImage || '',
        planPrice,
        strikeOffPrice,
        qty,
        itemType: cartItemType,
      };
    });

    return accessoriesList;
  };

  const getLines = useMemo(() => {
    const lineObject = allLines.map((line) => {
      const cartLine = lineDetails.lineInfo[line] || {};

      const {
        serviceInfo = {},
        totalPriceOfPlanAndPerks,
        deviceItem = {},
        accessories = [],
        shipping = {},
        shippingItem = {},
        mtnDetails = {},
      } = cartLine;

      const { newPricePlanInfo = {}, planDetails, planPerks = [], primaryUserInfo = {}, cart5GAddress = {}, emailAPiResponse = {} } = serviceInfo;
      const visFeature = serviceInfo?.selectedFeaturesList?.visFeature || [];
      const { isHomePhone = false } = deviceItem;

      return {
        plan: getPlanInfo(newPricePlanInfo, planDetails, visFeature, serviceInfo, totalPriceOfPlanAndPerks),
        accessories: accessories.length > 0 ? getAccessories(accessories) : [],
        isHomePhone,
        mtnDetails,
        deviceItem,
        depletionType: deviceItem?.depletionType,
        SetUp: deviceItem?.depletionType === 'O' ? 'Professional Setup' : 'Self Setup',
        planPerks,
        primaryUserInfo,
        billingAddress: primaryUserInfo?.address,
        cart5GAddress,
        shippingAddress: shipping?.address,
        storeInfo: shipping?.storeInfo,
        shippingDescription: shippingItem?.description,
        emailAPiResponse,
        isManual,
      };
    });
    return lineObject;
  }, [isLoaded]);

  const eligible5GHomeBundle = useMemo(() => {
    const eligibleBundle = cart?.orderDetails?.eligible5GHomeBundle || [];
    return eligibleBundle;
  }, [isLoaded]);

  const subscriptionsOptions = acctMcsSubscription.map((scription) => {
    const { itemId, pricePlanId, price, description, recurringInd } = scription;
    const planInfo = planData && itemId ? planData[itemId] : null;

    return {
      itemId,
      pricePlanId,
      displayName: planInfo?.planName || scription.name,
      imageUrl: planInfo?.imageUrl || '',
      planPrice: `${priceFormat(price)}/mo`,
      price,
      strikeOffPrice: '',
      activateText: planInfo?.activateText || '',
      activeToolTip: planInfo?.activeToolTip || '',
      isRemove: true,
      description,
      recurringInd,
    };
  });

  const appliedPayments = useMemo(() => {
    // CXP is Inconsistent between the options and the selected payment type
    // GC: payments.GC || payments.GIFT_CARD,
    const result = {
      AC: payments.AC || payments.BTA,
      CR: payments.CR || payments.CREDIT_CARD,
      VD: payments.VD || payments.VZD,
      DD: payments.DD,
      PY: payments.PY || payments.PAYPAL,
      AP: payments.AP || payments.APPLE_PAY || payments.APPLEPAY,
      AF: payments.AF,
    };
    return result;
  }, [isLoaded, payments]);

  const allLinesInfo = getLines;

  const lineDetail = allLinesInfo.find((line) => line.isHomePhone === false);
  const lineDetailPhone = allLinesInfo.find((line) => line.isHomePhone === true);
  const isHomePhoneAvailable = !!lineDetailPhone?.isHomePhone;
  const isPaymentDetailsAvailable = payments.AC?.paymentType === 'AC' || payments.CR?.paymentType === 'CR' || payments.PY?.paymentType === 'PAYPAL' || payments.PAYPAL?.paymentType === 'PAYPAL';

  return {
    eligible5GHomeBundle,
    payments,
    totalDueToday,
    isPaymentNeeded,
    appliedPayments,
    isPaymentDetailsAvailable,
    acctSubscriptions: subscriptionsOptions || [],
    plan: lineDetail?.plan || {},
    accessories: lineDetail?.accessories || [],
    isHomePhone: lineDetail?.isHomePhone || false,
    deviceItem: lineDetail?.deviceItem || {},
    depletionType: lineDetail?.depletionType || '',
    SetUp: lineDetail?.SetUp || '',
    planPerks: lineDetail?.planPerks || [],
    primaryUserInfo: lineDetail?.primaryUserInfo || {},
    billingAddress: lineDetail?.billingAddress || {},
    cart5GAddress: lineDetail?.cart5GAddress || {},
    shippingAddress: lineDetail?.shippingAddress || {},
    storeInfo: lineDetail?.storeInfo || {},
    shippingDescription: lineDetail?.storeInfo?.storeName ? 'In-store pickup address' : lineDetail?.shippingDescription || '',
    isHomePhoneAvailable,
    homePhone: isHomePhoneAvailable ? lineDetailPhone : null,
    currentView,
    cartCreatorInfo,
    accountNumber,
    isValidPromoResponse: checkout?.isValidPromoResponse || {},
    approvalStatus,
    errors,
    errorInfo,
  };
};
export const useContactDetailsHook = () => {
  const { contactInfo = {} } = useStore().getState();
  return {
    contactInfo,
  };
};

export const useFormHooksForValues = (formName) => {
  const existingState = useStore().getState();
  const { forms = {} } = existingState || {};
  const form = forms[formName];
  return {
    form,
  };
};
